import { Optional } from '@omni/kit/Types';
import {
  KitButton,
  KitIcon,
  KitInput,
  KitLoader,
  KitModal,
  KitSnack,
  KitText,
  KitTouchable,
} from '@omni/kit/components';
import KitConfirmModal from '@omni/kit/components/KitConfirmModal';
import { KitSnackDuration } from '@omni/kit/components/KitSnack.types';
import KitSnackRender from '@omni/kit/components/KitSnackRender';
import { useShellContext } from '@omni/kit/contexts/ShellContext';
import useShellAuthProviders from '@omni/kit/contexts/ShellContext/hooks/useShellAuthProviders';
import { useTranslation } from '@omni/kit/hooks/useTranslation';
import BaseService, { IHttpResponse } from '@omni/kit/services/BaseService';
import BorderRadius from '@omni/kit/theming/BorderRadius';
import Colors from '@omni/kit/theming/Colors';
import Spacing from '@omni/kit/theming/Spacing';
import { notifyAppUserSettingsUpdated } from '@omni/kit/utilities/NativeHelpers';
import {
  capitalizeFirstLetter,
  handleHttpErrorResponse,
} from '@omni/kit/utilities/utilities';
import React, {
  ReactElement,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  Alert,
  Platform,
  StyleSheet,
  Text,
  TextInput,
  View,
} from 'react-native';
import {
  CodeField,
  Cursor,
  useBlurOnFulfill,
  useClearByFocusCell,
} from 'react-native-confirmation-code-field';
import { KeyboardAwareScrollView } from 'react-native-keyboard-aware-scroll-view';
import Modal from 'react-native-modal';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

import { useBlockPageContext } from '../../../../BlockPageContext';
import { UpdateActionInterface } from '../../../types';

const CELL_COUNT = 6;

export interface ChangeEmailModalProps {
  appKey: string;
  clientBrandColor?: string;
  email: string;
  profileID?: string;
  setRefreshing?: (refreshing: boolean) => void;
  setVisible: (visible: boolean) => void;
  /** The action to perform when a user changes their email address. */
  updateEmailAction?: UpdateActionInterface;
  visible: boolean;
}

const ChangeEmailModal: React.FC<ChangeEmailModalProps> = ({
  appKey,
  clientBrandColor,
  email,
  profileID,
  setRefreshing,
  setVisible,
  updateEmailAction,
  visible,
}): ReactElement => {
  // ---------------------------------------------------------------------------
  // Global State
  const { t } = useTranslation(['blocks']);

  const { tokens } = useShellContext();
  const { targetProviders } = useShellAuthProviders();
  const { authProviderId } = useBlockPageContext();
  const { bottom: insetsBottom } = useSafeAreaInsets();

  const targetProviderToken = useMemo(
    () =>
      authProviderId
        ? tokens.accessTokens[authProviderId] || undefined
        : undefined,
    [authProviderId, tokens.accessTokens]
  );

  // ---------------------------------------------------------------------------
  // Local State
  const newEmailRef = useRef<TextInput>(null);

  const [value, setValue] = useState<string>('');
  const ref = useBlurOnFulfill({ value, cellCount: CELL_COUNT + 1 });
  const [isLoading, setIsLoading] = useState(false);
  const [snackOptions, setSnackOptions] = useState({
    visible: false,
    message: '',
    duration: KitSnackDuration.SHORT,
    marginBottom: Spacing.l,
  });
  const [confirmModalVisible, setConfirmModalVisible] = useState(false);
  const [confirmActionKey] = useState('');

  const [resendVerificationCodeButton, setResendVerificationCodeButton] =
    useState(false);
  const [newEmail, setNewEmail] = useState<string>('');
  const [verificationModalVisible, setVerificationModalVisible] =
    useState(false);

  // ---------------------------------------------------------------------------
  // Computed State
  const isValidEmail = useMemo((): boolean => {
    /** Check if the email is valid */
    const emailRegex = /^.+@.+\..+/g;

    return Boolean(
      newEmail.match(emailRegex) &&
        newEmail?.charAt(0) !== '!' &&
        newEmail !== email
    );
  }, [newEmail, email]);

  const [codeFieldProps, getCellOnLayoutHandler] = useClearByFocusCell({
    value,
    setValue,
  });

  const handleVerificationCode = (
    success = true,
    error: Optional<string> = null
  ): void => {
    /**
     *  Based on email-account-verification-code request we decide what interactions should
     *  happen for the user regarding the error type (verificationCodeInvalid, codeExpired or tooManyAttempts)
     */
    setIsLoading(false);
    setValue('');

    if (success) {
      setRefreshing?.(true);
      setNewEmail('');
      setVerificationModalVisible(false);
      KitSnack.show(t('blocks:changeEmail.emailUpdated'));
    } else {
      if (error === 'verification_code_invalid') {
        showKitSnack(
          t('blocks:changeEmail.error.invalid'),
          KitSnackDuration.SHORT
        );
      } else if (error === 'verification_code_expired') {
        setResendVerificationCodeButton(true);
        showKitSnack(
          t('blocks:changeEmail.error.expired'),
          KitSnackDuration.SHORT
        );
      } else if (error === 'verification_code_too_many_attempts') {
        setVerificationModalVisible(false);
        KitSnack.show(t('blocks:changeEmail.error.tooManyAttempts'));
      } else if (error === 'email_already_exists') {
        setVerificationModalVisible(false);
        KitSnack.show(t('blocks:changeEmail.error.emailExists'));
      } else {
        showKitSnack(
          t('blocks:changeEmail.error.default'),
          KitSnackDuration.SHORT
        );
      }
    }
  };

  const handleEmailUpdated = (
    success = true,
    message: Optional<string> = null
  ): void => {
    if (success) {
      setVisible(false);
      // Server takes a while before returning the updated data
      setTimeout(() => {
        setRefreshing?.(true); // auto-refresh the entire screen to display new email if provided by backend
        notifyAppUserSettingsUpdated();
        setVerificationModalVisible(true);
      }, 500);
    } else {
      showKitSnack(
        message?.length
          ? capitalizeFirstLetter(message)
          : t('blocks:changeEmail.error.failedSave'),
        KitSnackDuration.SHORT
      );
    }
  };

  const makeRequestForChangeEmail = async (): Promise<IHttpResponse> => {
    // make a request with feeds endpoint to update email
    const originalAction = updateEmailAction;

    if (originalAction?.updateUrl && originalAction?.settings?.[0]) {
      const settings = [...(originalAction?.settings ?? [{}])];
      settings[0] = {
        ...settings[0],
        value: newEmail.trim(),
        old_email: email,
      };

      const url = originalAction.updateUrl;

      return await BaseService.Put({
        url: url as string,
        appKey,
        authProviderId,
        targetProviders,
        token: targetProviderToken,
        data: { profileID, settings },
        headers: {
          'Content-Type': 'application/json',
        },
      });
    } else {
      throw new Error();
    }
  };

  const saveNewEmail = async (): Promise<void> => {
    const res = await makeRequestForChangeEmail();

    const ok = res.status === 201 || res.status === 200;

    if (ok) {
      handleEmailUpdated();
    } else {
      const { message } = await handleHttpErrorResponse(res);
      handleEmailUpdated(false, message);
    }
  };

  const resendCode = async (): Promise<void> => {
    const res = await makeRequestForChangeEmail();

    const ok = res.status === 201 || res.status === 200;

    if (ok) {
      setResendVerificationCodeButton(false);
      showKitSnack(t('blocks:changeEmail.emailResent'), KitSnackDuration.SHORT);
    } else {
      const { message } = await handleHttpErrorResponse(res);
      showKitSnack(
        message ?? t('blocks:changeEmail.error.failedSave'),
        KitSnackDuration.SHORT
      );
    }
  };

  const showKitSnack = (msg: string, dur: number): void => {
    setSnackOptions({
      duration: dur,
      marginBottom: Spacing.l,
      message: msg,
      visible: true,
    });
  };

  const closeVerificationModal = (): void => {
    if (Platform.OS === 'web') {
      setConfirmModalVisible(true);
    } else {
      Alert.alert(
        t('blocks:changeEmail.alert.title'),
        t('blocks:changeEmail.alert.body'),
        [
          {
            text: t('blocks:changeEmail.alert.no'),
            onPress: () => setVerificationModalVisible(true),
            style: 'cancel',
          },
          {
            text: t('blocks:changeEmail.alert.yes'),
            onPress: () => {
              setVerificationModalVisible(false);
              setResendVerificationCodeButton(false);
              setIsLoading(false);
              setValue('');
              setNewEmail('');
            },
          },
        ]
      );
    }
  };

  const closeVerificationModalWeb = () => {
    setConfirmModalVisible(false);
    setVerificationModalVisible(false);
    setResendVerificationCodeButton(false);
    setIsLoading(false);
    setValue('');
    setNewEmail('');
  };

  useEffect((): void => {
    // make a call to confirm activation code only if all the numbers are completed
    if (value?.length === 6) {
      setIsLoading(true);

      async function confirmVerificationCode(): Promise<void> {
        const originalAction = updateEmailAction;

        if (originalAction?.confirmEmailAccountVerificationCodeUrl) {
          const url = originalAction.confirmEmailAccountVerificationCodeUrl;

          const res = await BaseService.Put({
            url: url as string,
            appKey,
            authProviderId,
            targetProviders,
            token: targetProviderToken,
            data: { code: value, email: email },
            headers: {
              'Content-Type': 'application/json',
            },
          });

          const ok = res.status === 201 || res.status === 200;

          if (ok) {
            handleVerificationCode();
          } else {
            const { data } = await handleHttpErrorResponse(res);
            handleVerificationCode(false, data?.error);
          }
        }
      }
      confirmVerificationCode();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  useEffect((): void => {
    // Fix for android to auto-focus the input on open
    if (visible) {
      if (Platform.OS === 'android') {
        setTimeout(() => newEmailRef.current?.focus(), 150);
      } else {
        newEmailRef.current?.focus();
      }
    } else {
      newEmailRef.current?.blur();
    }
  }, [visible]);

  return (
    <>
      <KitModal visible={visible} setVisible={setVisible}>
        <KeyboardAwareScrollView
          style={{ flex: 1 }}
          contentContainerStyle={styles.scrollContainer}
          keyboardShouldPersistTaps='handled'
          enableOnAndroid={true}
          showsVerticalScrollIndicator={false}
          enableAutomaticScroll={
            Platform.OS === 'android'
              ? Platform.Version < 31 || Platform.Version > 34
              : false // Android 31 - 34
          }
          extraScrollHeight={Platform.OS === 'android' ? insetsBottom : 0}
        >
          <View style={{ margin: Spacing.xl }}>
            <KitText h1 testID='modalEmailAddressTitle'>
              {t('blocks:changeEmail.updateEmail')}
            </KitText>
            <KitInput
              autoCapitalize='none'
              inputRef={newEmailRef}
              label={t('blocks:changeEmail.newEmail')}
              marginVertical={18}
              onChangeText={setNewEmail}
              testID='NewEmailAddress'
              value={newEmail}
            />
            <KitButton
              color={clientBrandColor}
              disabled={!isValidEmail}
              onPress={saveNewEmail}
              testID='SetEmailButton'
              title={t('blocks:changeEmail.setEmail')}
            />
          </View>
          <KitSnackRender
            {...snackOptions}
            setVisible={(value) =>
              setSnackOptions({ ...snackOptions, visible: value })
            }
          />
        </KeyboardAwareScrollView>
      </KitModal>

      <KitModal
        setVisible={closeVerificationModal}
        visible={verificationModalVisible}
      >
        <KeyboardAwareScrollView
          style={{ flex: 1 }}
          contentContainerStyle={styles.scrollContainer}
          keyboardShouldPersistTaps='handled'
          enableOnAndroid={true}
          showsVerticalScrollIndicator={false}
          enableAutomaticScroll={
            Platform.OS === 'android' ? Platform.Version > 34 : false // Android >14
          }
          extraScrollHeight={Platform.OS === 'android' ? insetsBottom : 0}
        >
          <View style={styles.verificationModalContainer}>
            <View style={{ display: 'flex', alignItems: 'center' }}>
              <KitIcon
                color={Colors.N300}
                name='email'
                size={40}
                style={{ marginBottom: Spacing.s }}
              />
              <KitText h1 testID='modalVerificationEmailTitle'>
                {t('blocks:changeEmail.emailSent')}
              </KitText>
              <KitText
                style={{
                  display: 'flex',
                  justifyContent: 'center',
                  marginTop: Spacing.m,
                  maxWidth: '80%',
                  textAlign: 'center',
                }}
              >
                {t('blocks:changeEmail.checkEmail', { email: newEmail })}
              </KitText>
            </View>
            <View
              style={{
                alignItems: 'center',
                display: isLoading ? 'flex' : 'none',
                marginBottom: Spacing.xl,
                marginTop: Spacing.xl,
                opacity: isLoading ? 1 : 0,
              }}
            >
              {isLoading ? <KitLoader large /> : null}
            </View>
            <View
              style={{
                display:
                  resendVerificationCodeButton || isLoading ? 'none' : 'flex',
                flexDirection: 'row',
                justifyContent: 'center',
                marginBottom: Spacing.xl,
                marginLeft: Spacing.m,
                marginTop: Spacing.xl,
                opacity: resendVerificationCodeButton || isLoading ? 0 : 1,
              }}
            >
              <CodeField
                {...codeFieldProps}
                caretHidden={false}
                cellCount={CELL_COUNT}
                keyboardType='number-pad'
                onChangeText={setValue}
                ref={ref}
                renderCell={({ index, symbol, isFocused }) => (
                  <Text
                    key={index}
                    onLayout={getCellOnLayoutHandler(index)}
                    style={[styles.cell, isFocused && styles.focusCell]}
                  >
                    {symbol || (isFocused ? <Cursor /> : null)}
                  </Text>
                )}
                testID='ConfirmationCodeField'
                textContentType='oneTimeCode'
                value={value}
              />
            </View>
            <View
              style={{
                display: resendVerificationCodeButton ? 'flex' : 'none',
                marginBottom: Spacing.xl,
                marginTop: Spacing.xl,
                opacity: resendVerificationCodeButton ? 1 : 0,
              }}
            >
              <KitButton
                onPress={resendCode}
                secondary
                title={t('blocks:changeEmail.resendCode')}
              />
            </View>

            <View
              pointerEvents={isLoading ? 'none' : 'auto'}
              style={{
                display: 'flex',
                flexDirection: 'row',
                justifyContent: 'center',
                opacity: isLoading ? 0.5 : 1,
              }}
            >
              <KitText style={{ marginRight: Spacing.xs }}>
                {t('blocks:changeEmail.didntReceiveEmail')}
              </KitText>
              <KitTouchable onPress={resendCode}>
                <KitText style={{ color: '#4f64ff' }} testID='ResendCode'>
                  {t('blocks:changeEmail.resendCodeShort')}
                </KitText>
              </KitTouchable>
            </View>
          </View>
          <KitSnackRender
            {...snackOptions}
            setVisible={(value) =>
              setSnackOptions({ ...snackOptions, visible: value })
            }
          />
        </KeyboardAwareScrollView>
      </KitModal>
      <Modal
        animationIn='fadeIn'
        animationOut='fadeOut'
        isVisible={confirmModalVisible}
      >
        <KitConfirmModal
          action={confirmActionKey}
          cancelButtonText={t('blocks:changeEmail.alert.no')}
          confirmButtonText={t('blocks:changeEmail.alert.yes')}
          message={t('blocks:changeEmail.alert.body')}
          onConfirm={closeVerificationModalWeb}
          setConfirmModalVisible={setConfirmModalVisible}
          title={t('blocks:changeEmail.alert.title')}
        />
      </Modal>
    </>
  );
};

const styles = StyleSheet.create({
  cell: {
    backgroundColor: Colors.N75,
    borderRadius: BorderRadius.m,
    color: Colors.N900,
    fontSize: 32,
    height: 54,
    marginRight: Spacing.m,
    overflow: 'hidden',
    paddingTop: Platform.OS === 'ios' ? 8 : 5,
    textAlign: 'center',
    width: 44,
  },
  focusCell: { color: Colors.brand },
  verificationModalContainer: {
    marginBottom: Spacing.xxl,
    marginLeft: Spacing.xl,
    marginRight: Spacing.xl,
    marginTop: Spacing.xxl,
  },
  scrollContainer: {
    flexGrow: 1,
    justifyContent: 'center',
  },
});

export default ChangeEmailModal;
